import React, { useCallback } from "react";
import PropTypes from "prop-types";
import "../../App.css";

const Card = ({ title, level, levelIcon, description, onClick, children, badgeCount, badgeText }) => {
  return (
    <article className="card" style={{ cursor: "default", position: "relative" }}>
      {(badgeCount > 0 || badgeText) && (
        <div
          className="card__badge"
          title={badgeText ? badgeText : `${badgeCount} Pending Submissions${badgeCount > 1 ? 's' : ''}`}
        >
          <span className="badge-pulse-dot"></span>
          {badgeText ? badgeText : `${badgeCount} Pending Submissions${badgeCount > 1 ? 's' : ''}`}
        </div>
      )}
      <div className="card__inner" onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}      >
        <div className="card__content flow">
          <div className="card__content--container flow">
            {level && (
              <div className="card__level-badge-container">
                <span className="card__level-badge" title={typeof level === "string" ? `Level: ${level}` : undefined}>
                  <i className={`fa-solid ${levelIcon || "fa-graduation-cap"} card__level-icon`}></i>
                  <span>{level}</span>
                </span>
              </div>
            )}
            <h2
              className="card__title"
              onClick={onClick}
              style={{ cursor: onClick ? "pointer" : "default" }}
            >
              {title}
            </h2>
            <hr className="card__hr" />
            {description && (
              <div className="card__description">{description}</div>
            )}
          </div>
        </div>
      </div>
      {children && <div className="card__actions">{children}</div>}
    </article>
  );
};

const ActionCard = ({ title, onAction, children }) => {
  return (
    <article
      className="card action-card"
      style={{ cursor: "default" }}
      onClick={onAction}
    >
      <div className="card__content flow">
        <div className="card__content--container flow">
          <h2
            className="card__action-title"
            onClick={onAction}
            style={{ cursor: "pointer", fontWeight: "bold" }}
          >
            <img
              src="/imgs/add_icon.svg"
              alt="add_icon"
              width={100}
              style={{ filter: "invert(1)" }}
            />{" "}
            <br />
            {title}
          </h2>
        </div>
        {children && <div className="card__actions">{children}</div>}
      </div>
    </article>
  );
};

const CardList = ({ cards }) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filteredCards, setFilteredCards] = React.useState(cards);

  // Helper function to extract text from React nodes
  const extractText = useCallback((element) => {
    if (typeof element === "string") {
      return element;
    } else if (Array.isArray(element)) {
      return element.map(extractText).join(" ");
    } else if (React.isValidElement(element) && element.props.children) {
      return extractText(element.props.children);
    }
    return "";
  }, []);

  // Helper function to get all searchable text from a card
  const getCardText = useCallback(
    (card) => {
      let text = "";

      Object.keys(card).forEach((key) => {
        const value = card[key];
        if (typeof value === "string") {
          text += `${value} `;
        } else if (React.isValidElement(value)) {
          text += `${extractText(value)} `;
        }
      });

      return text.toLowerCase();
    },
    [extractText]
  );

  React.useEffect(() => {
    const lowercasedTerm = searchTerm.toLowerCase();
    const filtered = cards.filter((card) => {
      if (card.isActionCard) return true; // Always show action card
      const cardText = getCardText(card);
      return cardText.includes(lowercasedTerm);
    });
    setFilteredCards(filtered);
  }, [searchTerm, cards, getCardText]);

  return (
    <div>
      <div className="table-search">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      <div className="cards-container">
        {filteredCards.length > 0 ? (
          filteredCards.map((card) =>
            card.isActionCard ? (
              <ActionCard
                key={card.id}
                title={card.title}
                onAction={card.onAction}
              >
                {card.children}
              </ActionCard>
            ) : (
              <Card
                key={card.id}
                title={card.title}
                level={card.level}
                levelIcon={card.levelIcon}
                description={card.description}
                onClick={card.onClick}
                badgeCount={card.badgeCount}
                badgeText={card.badgeText}
              >
                {card.children}
              </Card>
            )
          )
        ) : (
          <p className="error" style={{ fontSize: "2rem" }}>
            No matching records found.
          </p>
        )}
      </div>
    </div>
  );
};

// Define PropTypes for regular Card
const regularCardShape = PropTypes.shape({
  level: PropTypes.node,
  levelIcon: PropTypes.string,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  onClick: PropTypes.func,
  children: PropTypes.node,
  isActionCard: PropTypes.bool,
});

// Define PropTypes for ActionCard
const actionCardShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onAction: PropTypes.func.isRequired,
  children: PropTypes.node,
  isActionCard: PropTypes.bool,
});

// Update PropTypes for Card
Card.propTypes = {
  level: PropTypes.node,
  levelIcon: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  onClick: PropTypes.func,
  children: PropTypes.node,
  badgeCount: PropTypes.number,
  badgeText: PropTypes.string,
};

// Update PropTypes for ActionCard
ActionCard.propTypes = {
  title: PropTypes.string.isRequired,
  onAction: PropTypes.func.isRequired,
  children: PropTypes.node,
};

// Update PropTypes for CardList
CardList.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.oneOfType([regularCardShape, actionCardShape])
  ).isRequired,
};

export { Card, ActionCard, CardList };
